type DateTimeParts = { year: number; month: number; day: number; hour: number; minute: number; second: number }

function partsAt(instant: Date, timeZone: string): DateTimeParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
    minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(instant)
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(p => p.type === type)?.value)
  return { year: value('year'), month: value('month'), day: value('day'), hour: value('hour'), minute: value('minute'), second: value('second') }
}

function sameMinute(a: DateTimeParts, b: DateTimeParts): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day && a.hour === b.hour && a.minute === b.minute
}

function matchingInstants(date: string, time: string, timeZone: string): number[] {
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = time.split(':').map(Number)
  const wanted = { year, month, day, hour, minute, second: 0 }
  if (![year, month, day, hour, minute].every(Number.isFinite)) throw new Error('Enter a valid punch time')

  // All civil timezone offsets are within 14 hours of UTC. Searching that
  // bounded window also returns both instants during a repeated fall-back
  // minute, instead of silently collapsing the wall time to one occurrence.
  const wallAsUTC = Date.UTC(year, month - 1, day, hour, minute)
  const matches: number[] = []
  for (let candidate = wallAsUTC - 14 * 3_600_000; candidate <= wallAsUTC + 14 * 3_600_000; candidate += 60_000) {
    if (sameMinute(partsAt(new Date(candidate), timeZone), wanted)) matches.push(candidate)
  }
  return matches
}

/** Convert company-local wall time to an instant without assuming a fixed UTC offset. */
export function zonedPunchTime(date: string, time: string, timeZone: string): string {
  const matches = matchingInstants(date, time, timeZone)
  if (matches.length === 0) {
    throw new Error('That local time does not exist because of daylight saving time')
  }
  return new Date(matches[0]).toISOString()
}

export function punchTimeInZone(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return ''
  return new Intl.DateTimeFormat('en-CA', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso))
}

export function punchDateInZone(iso: string, timeZone: string): string {
  const parts = partsAt(new Date(iso), timeZone)
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`
}

/**
 * Build a shift's two punch instants from ONE date plus two wall times.
 *
 * The edit form carries a single Date field, so the punch inputs are time-only:
 * a second date picker on the punches could disagree with it, and
 * `timesheet_entries.date` (which buckets payroll) would silently diverge from
 * the punches it is supposed to describe.
 *
 * A clock-out at or before the clock-in means the shift ran past midnight, so
 * it lands on the next day — the only way an overnight shift can be expressed
 * against one date. The next day is resolved off noon so the DST edge (adding
 * 24h to midnight landing back on the same calendar day) can't bite.
 *
 * Either time may be empty: an entry can have a date and hours with no punches
 * at all. Throws for a wall time that doesn't exist in this zone (spring-forward).
 */
export function buildShiftPunches(
  date: string, inTime: string, outTime: string, timeZone: string,
): { clockIn: string | null; clockOut: string | null } {
  const clockIn = inTime && date ? zonedPunchTime(date, inTime, timeZone) : null
  let clockOut = outTime && date ? zonedPunchTime(date, outTime, timeZone) : null
  if (clockIn && clockOut && Date.parse(clockOut) <= Date.parse(clockIn)) {
    const nextDay = new Date(Date.parse(`${date}T12:00:00Z`) + 86_400_000)
    clockOut = zonedPunchTime(nextDay.toISOString().slice(0, 10), outTime, timeZone)
  }
  return { clockIn, clockOut }
}

/**
 * Resolve a wall time the user is declaring for a punch that has ALREADY
 * happened ("I actually started at 07:00", "I actually finished at 17:00").
 *
 * The punch can't be in the future, so an HH:mm that hasn't come round yet
 * today must mean yesterday — clocking out at 00:20 for a shift that ended
 * 23:30 has to resolve to last night, not tonight. Without this the server
 * rejects it as a future time and the crew member is stuck.
 *
 * `reference` is normally now; pass the shift start when resolving a
 * clock-out so the roll-back is measured from the punch it belongs to.
 * TS-PUNCH-TIME-EDIT — docs/features/timesheets-punch-time-edit.md.
 */
export function resolvePastPunch(referenceIso: string, wallTime: string, timeZone: string): string {
  const resolved = resolveEditedPunch(referenceIso, wallTime, timeZone)
  if (Date.parse(resolved) <= Date.now()) return resolved
  const dayEarlier = new Date(Date.parse(referenceIso) - 24 * 3_600_000).toISOString()
  return resolveEditedPunch(dayEarlier, wallTime, timeZone)
}

/**
 * Resolve an edited wall time relative to its original punch. Keeping an
 * unchanged instant intact is important during the repeated fall-back hour:
 * HH:mm alone cannot identify which occurrence the original punch used.
 */
export function resolveEditedPunch(originalIso: string, editedTime: string, timeZone: string): string {
  if (editedTime === punchTimeInZone(originalIso, timeZone)) return originalIso
  const matches = matchingInstants(punchDateInZone(originalIso, timeZone), editedTime, timeZone)
  if (matches.length === 0) throw new Error('That local time does not exist because of daylight saving time')
  const original = Date.parse(originalIso)
  // An edit within a repeated hour retains the original punch's occurrence by
  // selecting the candidate nearest that instant (and therefore its offset).
  const closest = matches.reduce((best, candidate) =>
    Math.abs(candidate - original) < Math.abs(best - original) ? candidate : best)
  return new Date(closest).toISOString()
}
